import React from 'react';
import { MdAdd, MdDeleteOutline, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import FillAppField from 'components/FillAppField';
import {
    type FillAppField as FieldDef,
    type FillAppRepeatingGroup,
    type ChoiceGroups,
    type FillAppValues,
    fieldIsVisible,
    resolveShowWhen,
    groupActiveSlots,
    slotCountKey,
} from 'global/fillapp';

import './index.css';

type Props = {
    group: FillAppRepeatingGroup;
    fieldsByQid: Record<string, FieldDef>;
    values: FillAppValues;
    cycleYear?: number;       // application cycle, for cycle-dependent widgets
    choiceGroups: ChoiceGroups;
    readOnly?: boolean;
    onChange?: (qid: string, value: unknown) => void;
    onBlur?: (qid: string) => void;
};

function titleCase(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function RepeatingGroup({
    group, fieldsByQid, values, cycleYear, choiceGroups, readOnly, onChange, onBlur,
}: Props) {
    const gateOk = !group.gate || resolveShowWhen(group.gate, values);
    const active = groupActiveSlots(group, values);

    const renderField = (qid: string) => {
        const field = fieldsByQid[qid];
        if (!field) return null;
        if (!fieldIsVisible(field, values)) return null;
        return (
            <FillAppField
                key={qid}
                field={field}
                value={values[qid]}
                values={values}
                cycleYear={cycleYear}
                choiceGroups={choiceGroups}
                readOnly={readOnly}
                onChange={onChange}
                onBlur={onBlur}
            />
        );
    };

    // Add: bump the active-slot count (button mode only).
    const handleAdd = () => {
        if (readOnly || !onChange || active >= group.maxSlots) return;
        onChange(slotCountKey(group), active + 1);
    };

    // Swap every field value between two entry slots (reorder up/down).
    const swapSlots = (a: number, b: number) => {
        if (readOnly || !onChange) return;
        const sa = group.slots[a] || [];
        const sb = group.slots[b] || [];
        const len = Math.max(sa.length, sb.length);
        for (let k = 0; k < len; k++) {
            const qa = sa[k];
            const qb = sb[k];
            const va = qa != null ? (values[qa] ?? '') : '';
            const vb = qb != null ? (values[qb] ?? '') : '';
            if (qa != null) onChange(qa, vb);
            if (qb != null) onChange(qb, va);
        }
    };

    // Remove entry `idx`: shift later entries up into its qids, clear the last
    // entry, then drop the count by one (exact Common App renumber behavior).
    const handleRemove = (idx: number) => {
        if (readOnly || !onChange) return;
        for (let j = idx; j < active - 1; j++) {
            const dst = group.slots[j];
            const src = group.slots[j + 1];
            dst.forEach((dq, k) => {
                const sq = src[k];
                onChange(dq, sq != null ? (values[sq] ?? '') : '');
            });
        }
        (group.slots[active - 1] || []).forEach(q => onChange(q, ''));
        if (group.mode === 'button') onChange(slotCountKey(group), active - 1);
        else if (group.countQid) onChange(group.countQid, String(active - 1));
    };

    const canAdd = group.mode === 'button' && !readOnly && gateOk && active < group.maxSlots;
    const canRemove = group.mode === 'button' && !readOnly && active > group.minSlots;
    const canReorder = group.mode === 'button' && !readOnly && active > 1;
    const remaining = group.maxSlots - active;

    return (
        <div className='rgroup'>
            {/* Gate / count question and any other shared fields render always. */}
            {group.sharedQids.map(renderField)}

            {gateOk && Array.from({ length: active }).map((_, i) => (
                <div key={i} className='rgroup__slot'>
                    <div className='rgroup__slotHead'>
                        <span className='rgroup__slotTitle'>
                            {titleCase(group.label)} {i + 1}
                        </span>
                        {!readOnly && (
                            <div className='rgroup__slotActions'>
                                {canReorder && (
                                    <button
                                        type='button'
                                        className='rgroup__iconBtn'
                                        title={`Move ${group.label} ${i + 1} up`}
                                        aria-label={`Move ${group.label} ${i + 1} up`}
                                        disabled={i === 0}
                                        onClick={() => swapSlots(i, i - 1)}
                                    >
                                        <MdArrowUpward />
                                    </button>
                                )}
                                {canReorder && (
                                    <button
                                        type='button'
                                        className='rgroup__iconBtn'
                                        title={`Move ${group.label} ${i + 1} down`}
                                        aria-label={`Move ${group.label} ${i + 1} down`}
                                        disabled={i === active - 1}
                                        onClick={() => swapSlots(i, i + 1)}
                                    >
                                        <MdArrowDownward />
                                    </button>
                                )}
                                {canRemove && (
                                    <button
                                        type='button'
                                        className='rgroup__iconBtn rgroup__iconBtn--danger'
                                        title={`Remove ${group.label} ${i + 1}`}
                                        aria-label={`Remove ${group.label} ${i + 1}`}
                                        onClick={() => handleRemove(i)}
                                    >
                                        <MdDeleteOutline />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    {(group.slots[i] || []).map(renderField)}
                </div>
            ))}

            {canAdd && (
                <div className='rgroup__addRow'>
                    <button type='button' className='rgroup__add' onClick={handleAdd}>
                        <MdAdd /> Add another {group.label}
                    </button>
                    {group.softMax != null ? (
                        // Counted against the REAL Common App cap; going over is
                        // allowed (drafting space) but flagged in red.
                        <span className={`rgroup__available ${active > group.softMax ? 'rgroup__available--over' : ''}`}>
                            {active} of {group.softMax} available
                            {active > group.softMax && ' — over the Common App limit'}
                        </span>
                    ) : (
                        <span className='rgroup__available'>
                            {remaining} of {group.maxSlots} available
                        </span>
                    )}
                </div>
            )}

            {gateOk && group.trailingSharedQids.map(renderField)}
        </div>
    );
}

export default RepeatingGroup;
